import React from "react";
import { useLocation } from "react-router-dom";
import {
  BackNavRow,
  PageContainer,
  SingleLineText,
} from "../common/CommonStyles";
import {
  canSwitchBetweenProfiles,
  ProfileSwitcherMenu,
} from "../common/ProfileSwitcherMenu";
import {
  Lesson,
  isUserAnonymous,
  isSchoolUser,
  isUserSignedUp,
  SpeakingRate,
  CantPlay,
} from "../common/api";
import { Account, AccountContext } from "../common/context/AccountContext";
import { DataHandling } from "./DataHandling";
import { EnterFirstName } from "./EnterFirstName";
import {
  ClosedCaptionsState,
  PlaybackActions,
  PlaybackActionsPlayPauseState,
  WebRtcPlayerWrapper,
} from "../components/preview/WebRtcPlayerWrapper";
import { useTranslation } from "react-i18next";
import { PreferencesContext } from "../common/context/PreferencesContext";
import { ApiContext } from "../common/context/ApiContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { Tooltip } from "@mui/material";
import styled from "styled-components";
import {
  COLOR_SURFACE_CONTAINER,
  COLOR_BLACK,
  COLOR_GRAY_33,
  COLOR_WHITE,
} from "../common/colors";
import {
  faPlay,
  faPause,
  faCommentCaptions as faCommentCaptionsSolid,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LessonExecutionState } from "./LessonPage";
import { EditProfilePage } from "./EditProfilePage";
import { WebContentLessonPlayer } from "../components/lesson/WebContentLessonPlayer";
import {
  faDialMax,
  faDialMed,
  faDialMin,
  faCommentCaptions as faCommentCaptionsRegular,
  faChevronDown,
} from "@fortawesome/pro-regular-svg-icons";

const SHOW_PLAYBACK_LABELS = "@media (min-width: 600px)";

const PlaybackControlsRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
`;

const PlaybackButton = styled.button.withConfig({
  shouldForwardProp: (prop: string) => prop !== "isSelected",
})<{ isSelected?: boolean }>`
  flex-shrink: 0;
  height: 40px;
  min-width: 40px;
  border-radius: 20px;
  background: ${({ isSelected }) =>
    isSelected ? COLOR_GRAY_33 : COLOR_SURFACE_CONTAINER};
  padding: 0;
  cursor: pointer;
  font-weight: 400;
  font-size: 16px;
  color: ${({ isSelected }) => (isSelected ? COLOR_WHITE : COLOR_BLACK)};
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  :disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const LabeledPlaybackButton = styled(PlaybackButton)`
  ${SHOW_PLAYBACK_LABELS} {
    padding: 0 15px;
  }
`;

const PlayPauseButton = styled(LabeledPlaybackButton)`
  ${SHOW_PLAYBACK_LABELS} {
    min-width: 110px;
  }
`;

const ProfileSwitcherButton = styled(PlaybackButton)`
  max-width: 160px;
  padding: 0 12px 0 16px;
`;

const PlaybackButtonLabel = styled.span`
  display: none;

  ${SHOW_PLAYBACK_LABELS} {
    display: grid;
  }
`;

interface Props {
  setLessonExecutionState: (state: LessonExecutionState) => void;
  unitId: number | undefined;
  lesson: Lesson;
  lessonProfileId: number | null;
  account: Account;
}

export const LessonPagePlayer: React.FC<Props> = ({
  setLessonExecutionState,
  lesson,
  lessonProfileId,
  unitId,
  account,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const ignoreStateParam =
    new URLSearchParams(location.search).get("ignore_state") === "true";
  const { login } = React.useContext(AccountContext);
  const { makeGuestUser } = React.useContext(ApiContext);
  const { hasAcceptedData } = React.useContext(PreferencesContext);
  const [temporaryFirstName, setTemporaryFirstName] = React.useState<string>();
  const { startLoading, stopLoading } = useLoadingIndicator(false);
  const [playbackActions, setPlaybackActions] =
    React.useState<PlaybackActions>();
  const [speedTooltipTitle, setSpeedTooltipTitle] = React.useState("");
  const [isProfileMenuOpen, setIsProfileMenuOpen] = React.useState(false);
  const profileSwitcherRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (hasAcceptedData) {
      if (isUserAnonymous(account.user)) {
        startLoading();
        makeGuestUser().then((guest_user) => {
          login(guest_user.access_token, guest_user.user);
        });
      } else {
        if (!account.profile) {
          return;
        }
        if (account.profile.is_guest) {
          setTemporaryFirstName(localStorage.getItem("firstName") || "");
        }
        stopLoading();
      }
    }
  }, [
    hasAcceptedData,
    account,
    makeGuestUser,
    login,
    startLoading,
    stopLoading,
  ]);

  React.useEffect(() => {
    const speed =
      playbackActions !== undefined
        ? ": " +
          (playbackActions.speed === SpeakingRate.SLOW
            ? t("special_actions_playback_speed_slow")
            : playbackActions.speed === SpeakingRate.FAST
              ? t("special_actions_playback_speed_fast")
              : t("special_actions_playback_speed_normal"))
        : "";
    setSpeedTooltipTitle(t("special_actions_playback_speed") + speed);
  }, [t, playbackActions, setSpeedTooltipTitle]);

  const onLessonFinished = React.useCallback(
    (showPostLessonScreen: boolean, cantPlay: CantPlay | null) => {
      if (!showPostLessonScreen && cantPlay === null) {
        setLessonExecutionState({ type: "BEFORE_PLAY_LESSON_NOT_YET_STARTED" });
      } else {
        setLessonExecutionState({
          type: "AFTER_PLAY",
          cantPlay,
        });
      }
    },
    [setLessonExecutionState]
  );

  if (!hasAcceptedData) {
    return <DataHandling />;
  }

  if (
    isUserSignedUp(account.user) &&
    !isSchoolUser(account.user) &&
    !account.profile
  ) {
    return <EditProfilePage isForCreate={true} />;
  }
  if (!account.profile) {
    return <></>;
  }

  if (account.profile.is_guest && !temporaryFirstName) {
    return (
      <EnterFirstName
        back={() => {
          setLessonExecutionState({ type: "BEFORE_PLAY_FIRSTNAME" });
        }}
        setFirstName={(newFirstName) => {
          localStorage.setItem("firstName", newFirstName);
          setTemporaryFirstName(newFirstName);
        }}
      />
    );
  }

  if (lesson.web_content_url) {
    return (
      <PageContainer>
        <BackNavRow
          callback={() => {
            setLessonExecutionState({
              type: "BEFORE_PLAY_LESSON_NOT_YET_STARTED",
            });
          }}
          label={t("generic_nav_back")}
        >
          <h2>{lesson.title}</h2>
        </BackNavRow>
        <WebContentLessonPlayer
          webContentUrl={lesson.web_content_url}
          lessonId={lesson.id}
          lessonVersion={lesson.version}
          lessonTitle={lesson.title}
          profileId={account.profile.id}
        />
      </PageContainer>
    );
  }

  const isLessonReady = lessonProfileId === account.profile.id;

  const profileSwitcher = canSwitchBetweenProfiles(account) && (
    <>
      <ProfileSwitcherButton
        ref={profileSwitcherRef}
        disabled={!isLessonReady}
        aria-haspopup="menu"
        aria-expanded={isProfileMenuOpen}
        aria-label={t("special_actions_switch_profile")}
        onClick={() => setIsProfileMenuOpen(true)}
      >
        <SingleLineText>{account.profile.first_name}</SingleLineText>
        <FontAwesomeIcon icon={faChevronDown} />
      </ProfileSwitcherButton>
      <ProfileSwitcherMenu
        account={account}
        anchorEl={profileSwitcherRef.current}
        open={isProfileMenuOpen}
        showProfileManagement={false}
        onClose={() => setIsProfileMenuOpen(false)}
      />
    </>
  );

  const isPaused =
    playbackActions?.pauseState === PlaybackActionsPlayPauseState.Play;

  const playbackControls = playbackActions && (
    <PlaybackControlsRow>
      {playbackActions.closedCaptionsState !== undefined && (
        <Tooltip
          title={
            playbackActions.closedCaptionsState === ClosedCaptionsState.Enabled
              ? t("special_actions_closed_captions_on")
              : t("special_actions_closed_captions_off")
          }
        >
          <PlaybackButton
            isSelected={
              playbackActions.closedCaptionsState ===
              ClosedCaptionsState.Enabled
            }
            onClick={() => playbackActions.toggleClosedCaptions?.()}
          >
            <FontAwesomeIcon
              icon={
                playbackActions.closedCaptionsState ===
                ClosedCaptionsState.Enabled
                  ? faCommentCaptionsSolid
                  : faCommentCaptionsRegular
              }
            />
          </PlaybackButton>
        </Tooltip>
      )}
      <Tooltip title={speedTooltipTitle}>
        <LabeledPlaybackButton onClick={() => playbackActions.toggleSpeed()}>
          <FontAwesomeIcon
            icon={
              playbackActions.speed === SpeakingRate.SLOW
                ? faDialMin
                : playbackActions.speed === SpeakingRate.NORMAL
                  ? faDialMed
                  : faDialMax
            }
          />
          <PlaybackButtonLabel>
            {t("special_actions_playback_speed")}
          </PlaybackButtonLabel>
        </LabeledPlaybackButton>
      </Tooltip>
      <PlayPauseButton
        isSelected={isPaused}
        disabled={
          playbackActions.pauseState ===
          PlaybackActionsPlayPauseState.PauseDisabled
        }
        onClick={() =>
          isPaused ? playbackActions.clickPlay() : playbackActions.clickPause()
        }
      >
        <FontAwesomeIcon icon={isPaused ? faPlay : faPause} />
        <PlaybackButtonLabel>
          <span
            style={{
              gridArea: "1/1",
              visibility: isPaused ? "hidden" : "visible",
            }}
          >
            {t("special_actions_pause")}
          </span>
          <span
            style={{
              gridArea: "1/1",
              visibility: isPaused ? "visible" : "hidden",
            }}
          >
            {t("special_actions_play")}
          </span>
        </PlaybackButtonLabel>
      </PlayPauseButton>
    </PlaybackControlsRow>
  );

  return (
    <PageContainer className="custom-wide-container">
      <BackNavRow
        callback={() => {
          setLessonExecutionState({
            type: "BEFORE_PLAY_LESSON_NOT_YET_STARTED",
          });
        }}
        label={t("generic_nav_back")}
        rightChildren={playbackControls}
      >
        {profileSwitcher}
      </BackNavRow>
      <WebRtcPlayerWrapper
        key={account.profile.id}
        account={account}
        onLessonFinished={onLessonFinished}
        unitId={unitId}
        lessonId={lesson.id}
        customFlashpackId={lesson.custom_flashpack_id}
        temporaryFirstName={temporaryFirstName}
        isGuest={account.profile.is_guest}
        isLessonReady={isLessonReady}
        version={lesson.version}
        ignoreState={account.profileState === null || ignoreStateParam}
        setPlaybackActions={setPlaybackActions}
      />
    </PageContainer>
  );
};
